import { Link } from "react-router-dom";

// 5 logo mockups for 901 Lots.
// Each logo is rendered three ways:
//   1. Small mark only (favicon / app icon scale)
//   2. Full lockup on light background (navbar context)
//   3. Full lockup on dark background (footer context)

const NAME = "901 Lots";

// ---------- Option A — "Block M" (CURRENT) ----------
function MarkA({ size = 36 }) {
  return (
    <span
      className="inline-flex items-center justify-center rounded-md bg-blue-700 text-white font-display font-black leading-none"
      style={{ height: size, width: size, fontSize: size * 0.55 }}
    >
      M
    </span>
  );
}

// ---------- Option B — "Striped M" ----------
function MarkB({ size = 36 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="901 Lots"
    >
      <rect width="48" height="48" rx="8" fill="#1d4ed8" />
      {/* Two diagonal stripes forming an M with a center notch */}
      <path d="M9 36 L18 12 L24 26 L30 12 L39 36" stroke="#ffffff" strokeWidth="3.5" fill="none" strokeLinecap="square" strokeLinejoin="miter" />
      {/* Yellow center-line accent */}
      <path d="M24 30 L24 36" stroke="#facc15" strokeWidth="3" strokeLinecap="square" />
    </svg>
  );
}

// ---------- Option C — "Road Pin" (map pin with stripe) ----------
function MarkC({ size = 36 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="901 Lots"
    >
      {/* Pin */}
      <path
        d="M24 4 C14 4 7 11 7 21 C7 32 24 44 24 44 C24 44 41 32 41 21 C41 11 34 4 24 4 Z"
        fill="#1d4ed8"
      />
      {/* Inner stripe (white pavement line) */}
      <rect x="22" y="13" width="4" height="16" fill="#ffffff" />
      {/* Yellow tip dot */}
      <circle cx="24" cy="21" r="3.5" fill="#facc15" />
    </svg>
  );
}

// ---------- Option D — "Lot Tile" (3 parking stripes) ----------
function MarkD({ size = 36 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="901 Lots"
    >
      <rect width="48" height="48" rx="8" fill="#0f172a" />
      {/* Three white stripes evoking a freshly painted parking lot */}
      <rect x="10" y="10" width="5" height="28" fill="#ffffff" />
      <rect x="21.5" y="10" width="5" height="28" fill="#ffffff" />
      <rect x="33" y="10" width="5" height="28" fill="#ffffff" />
      {/* Orange accent stripe across the bottom */}
      <rect x="6" y="40" width="36" height="3" fill="#ea580c" />
    </svg>
  );
}

// ---------- Option E — "Compass Star" (Mid-South + cardinal directions) ----------
function MarkE({ size = 36 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="901 Lots"
    >
      <circle cx="24" cy="24" r="22" fill="#1d4ed8" />
      {/* 4-point compass star */}
      <path
        d="M24 6 L27 22 L42 24 L27 26 L24 42 L21 26 L6 24 L21 22 Z"
        fill="#ffffff"
      />
      <circle cx="24" cy="24" r="2.5" fill="#ea580c" />
    </svg>
  );
}

// ---------- Wordmark presentations ----------
function WordmarkPlain({ dark = false }) {
  return (
    <span
      className={`font-display font-black tracking-tight text-xl ${
        dark ? "text-white" : "text-slate-900"
      }`}
    >
      {NAME}
    </span>
  );
}

function WordmarkAllCaps({ dark = false }) {
  return (
    <span
      className={`font-display font-black uppercase text-xl tracking-[0.06em] ${
        dark ? "text-white" : "text-slate-900"
      }`}
    >
      901 Lots
    </span>
  );
}

// ---------- Option F — Stripe-underlined wordmark only (no symbol) ----------
function WordmarkF({ dark = false }) {
  return (
    <span className="inline-flex flex-col items-start leading-none">
      <span
        className={`font-display font-black uppercase text-xl tracking-[0.04em] ${
          dark ? "text-white" : "text-slate-900"
        }`}
      >
        901 Lots
      </span>
      <span className="mt-1.5 inline-flex gap-1">
        <span className="block h-1 w-10 bg-orange-600 rounded-sm" />
        <span className="block h-1 w-6 bg-orange-600 rounded-sm" />
        <span className="block h-1 w-2 bg-orange-600 rounded-sm" />
      </span>
    </span>
  );
}

// ---------- Layout pieces ----------
function Lockup({ children, dark = false }) {
  return (
    <div
      className={`flex items-center gap-3 px-5 py-4 rounded-lg ${
        dark ? "bg-slate-900" : "bg-white border border-slate-200"
      }`}
    >
      {children}
    </div>
  );
}

function Option({ id, name, description, pros, cons, render }) {
  return (
    <article
      data-testid={`logo-option-${id}`}
      className="bg-white border border-slate-200 rounded-lg overflow-hidden"
    >
      <div className="px-6 py-5 border-b border-slate-200 flex items-baseline justify-between flex-wrap gap-2">
        <h2 className="font-display font-bold text-slate-900 text-xl">
          Option {id} — {name}
        </h2>
        <p className="text-sm text-slate-500">{description}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
        {render({ dark: false })}
        {render({ dark: true })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 px-6 pb-6 text-sm">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-blue-700 mb-1">
            Strengths
          </div>
          <ul className="space-y-1 text-slate-700">
            {pros.map((p) => (
              <li key={p}>· {p}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-orange-600 mb-1">
            Trade-offs
          </div>
          <ul className="space-y-1 text-slate-700">
            {cons.map((c) => (
              <li key={c}>· {c}</li>
            ))}
          </ul>
        </div>
      </div>
    </article>
  );
}

export default function LogoPreview() {
  return (
    <div data-testid="logo-preview-page" className="bg-slate-50 min-h-screen py-14">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="text-sm font-medium text-blue-700 hover:text-blue-800"
        >
          ← Back to site
        </Link>

        <h1 className="mt-4 font-display font-black text-slate-900 text-4xl sm:text-5xl">
          Logo Options
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Five distinct directions for 901 Lots. Each is shown on
          both light (navbar) and dark (footer) backgrounds. Tell me the
          option you like (or mix-and-match: symbol from one, wordmark from
          another) and I'll wire it into the site.
        </p>

        <div className="mt-10 space-y-8">
          <Option
            id="A"
            name="Block M (current)"
            description="Letterform inside a colored tile."
            pros={[
              "Simple, scales perfectly",
              "Already in production",
              "Easy to favicon",
            ]}
            cons={[
              "Generic — could be any 'M' brand",
              "No connection to pavement",
            ]}
            render={({ dark }) => (
              <Lockup dark={dark}>
                <MarkA size={36} />
                <WordmarkPlain dark={dark} />
              </Lockup>
            )}
          />

          <Option
            id="B"
            name="Striped M"
            description="The M itself drawn as a single brushstroke with an orange center-line cap — evokes a painted lot line."
            pros={[
              "Industry-relevant (stripe = the work you do)",
              "Memorable shape",
              "Works monochrome",
            ]}
            cons={[
              "Slightly more abstract",
              "Needs the orange accent to read fully",
            ]}
            render={({ dark }) => (
              <Lockup dark={dark}>
                <MarkB size={36} />
                <WordmarkPlain dark={dark} />
              </Lockup>
            )}
          />

          <Option
            id="C"
            name="Road Pin"
            description="Map-pin shape with a white pavement stripe and a yellow center dot. Local + industry in one mark."
            pros={[
              "Instantly reads 'local service business'",
              "Strong Google Business Profile vibe",
              "Easy to map / nav",
            ]}
            cons={[
              "Map-pin clichéd in service industries",
              "Less distinct in a crowded SERP",
            ]}
            render={({ dark }) => (
              <Lockup dark={dark}>
                <MarkC size={36} />
                <WordmarkPlain dark={dark} />
              </Lockup>
            )}
          />

          <Option
            id="D"
            name="Lot Tile"
            description="Three white stripes on a dark tile = a freshly painted parking lot, with an orange curb accent."
            pros={[
              "Literally what you sell, abstracted nicely",
              "Industrial / construction aesthetic",
              "Photographs well on truck wraps",
            ]}
            cons={[
              "No letterform — relies entirely on the wordmark for ID",
            ]}
            render={({ dark }) => (
              <Lockup dark={dark}>
                <MarkD size={36} />
                <WordmarkAllCaps dark={dark} />
              </Lockup>
            )}
          />

          <Option
            id="E"
            name="Compass Star"
            description="Compass-rose star with an orange center, evoking the Mid-South region and direction."
            pros={[
              "Strong regional identity",
              "Works as a single hero icon",
              "Distinctive at small sizes",
            ]}
            cons={[
              "More 'travel/local guide' than 'pavement contractor'",
              "Less directly tied to the work",
            ]}
            render={({ dark }) => (
              <Lockup dark={dark}>
                <MarkE size={36} />
                <WordmarkPlain dark={dark} />
              </Lockup>
            )}
          />

          <Option
            id="F"
            name="Wordmark + Stripes"
            description="No symbol — pure wordmark with three orange parking stripes underneath. Bold, modern, no clipart."
            pros={[
              "Most distinctive option in this set",
              "Reads as 'pavement' without saying it",
              "Cleanest on truck/shirt embroidery",
            ]}
            cons={[
              "No square icon — need separate favicon design",
              "Best at horizontal sizes (not square placements)",
            ]}
            render={({ dark }) => (
              <Lockup dark={dark}>
                <WordmarkF dark={dark} />
              </Lockup>
            )}
          />
        </div>

        <div className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-sm text-slate-700 leading-relaxed">
          <p>
            <strong>My take:</strong> Option D (Lot Tile) is the most
            on-brand for a striping business and scales nicely from favicon to
            truck wrap. Option F (Wordmark + Stripes) is the boldest and
            cleanest in the navbar — but you'd need a separate square mark
            for favicon and social. Option B (Striped M) is a strong middle
            ground that gives you a single mark + wordmark that work
            together.
          </p>
          <p className="mt-3">
            Reply with a letter (or "B mark + F wordmark") and I'll
            implement it across the whole site, including favicon and
            social-share images.
          </p>
        </div>
      </div>
    </div>
  );
}
